import { useState, useRef, useEffect } from 'react';
import { Navbar } from '@/components/layout/Navbar';
import { Send, Sparkles, MapPin, Calendar, Users, Coins, RefreshCw, Download } from 'lucide-react';
import OceanBackground from '@/components/background/OceanBackground';

const MOCK_INTENT = { destination: '杭州', duration: '4天3晚', travelers: '3人（父母同行）', budget: '≤5000', preferences: ['父母友好', '低步行', '舒适', '景点便利'] };
const MOCK_THINKING = [
  { step: '理解需求', detail: '杭州 · 4天3晚 · 3人 · 预算5000', done: true },
  { step: '约束校验', detail: '父母友好 | 低步行 | 预算可行性', done: true },
  { step: '候选召回', detail: '酒店 24 家 | 景点 18 个 | 餐厅 32 家', done: true },
  { step: '方案生成', detail: '生成 3 套可行方案...', done: false },
];
const MOCK_HOTELS = [
  { name: '西湖国宾馆', price: 1680, score: 94, tags: ['位置绝佳', '父母友好', '含早餐'] },
  { name: '杭州西湖君悦', price: 1450, score: 91, tags: ['湖景', '步行便利'] },
];

const TripStudio = () => {
  const [query, setQuery] = useState('');
  const [messages, setMessages] = useState([]);
  const [thinking, setThinking] = useState([]);
  const [isThinking, setIsThinking] = useState(false);
  const [result, setResult] = useState(null);
  const chatRef = useRef(null);

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [messages, thinking]);

  const handleSubmit = () => {
    if (!query.trim()) return;
    setMessages(prev => [...prev, { role: 'user', content: query }]);
    setQuery('');
    setIsThinking(true);
    setThinking([]);
    let i = 0;
    const interval = setInterval(() => {
      if (i < MOCK_THINKING.length) {
        setThinking(prev => [...prev, MOCK_THINKING[i]]);
        i++;
      } else {
        clearInterval(interval);
        setIsThinking(false);
        setResult({ intent: MOCK_INTENT, hotels: MOCK_HOTELS });
      }
    }, 500);
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #041426 0%, #061A2C 50%, #041426 100%)' }}>
      <Navbar />
      <OceanBackground />
      <div className="max-w-5xl mx-auto px-6 pt-24 pb-20 relative z-10">
        {/* Header — 极简 */}
        <div className="text-center mb-10">
          <h1 className="text-2xl font-semibold mb-2" style={{ color: 'rgba(255,255,255,0.94)', letterSpacing: '-0.025em' }}>AI 工作台</h1>
          <p className="text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>告诉我你的旅行计划，剩下的交给 AI</p>
        </div>

        {/* Input — 沉浸感输入框 */}
        <div className="mb-8">
          <div className="relative">
            <textarea
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), handleSubmit())}
              placeholder="我想去杭州 4 天，带父母，预算 5000，不想太累..."
              rows={3}
              className="w-full rounded-2xl px-5 py-4 text-sm resize-none focus:outline-none transition-all duration-300"
              style={{
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.1)',
                color: 'rgba(255,255,255,0.9)',
              }}
            />
            <button onClick={handleSubmit}
              className="absolute right-4 bottom-4 w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200"
              style={{ background: 'linear-gradient(135deg, #4AA7D8, #D8B15A)' }}>
              <Send className="w-4 h-4 text-white" />
            </button>
          </div>
          {/* Prompt chips */}
          <div className="flex gap-2 mt-3 justify-center flex-wrap">
            {['杭州 4天 · 亲子', '东京 7天 · 情侣', '三亚 5天 · 父母'].map(q => (
              <button key={q} onClick={() => setQuery(q)} className="chip">{q}</button>
            ))}
          </div>
        </div>

        {/* Content Area */}
        <div className="space-y-5">
          {/* Messages & Thinking */}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className="max-w-[80%] px-4 py-3 rounded-2xl text-sm" style={{
                background: m.role === 'user' ? 'linear-gradient(135deg, #4AA7D8, #3d82b0)' : 'rgba(255,255,255,0.06)',
                color: m.role === 'user' ? 'white' : 'rgba(255,255,255,0.85)',
                border: m.role === 'user' ? 'none' : '1px solid rgba(255,255,255,0.08)',
              }}>
                {m.content}
              </div>
            </div>
          ))}

          {/* Thinking Steps */}
          {thinking.length > 0 && (
            <div className="ocean-glass p-5">
              <div className="space-y-3">
                {thinking.map((t, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(57,201,130,0.2)' }}>
                      <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
                    </div>
                    <span className="text-xs font-medium" style={{ color: 'rgba(255,255,255,0.85)' }}>{t.step}</span>
                    <span className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>— {t.detail}</span>
                  </div>
                ))}
                {isThinking && (
                  <div className="flex gap-1.5 items-center pl-7">
                    <div className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: '#4AA7D8' }} />
                    <div className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: '#4AA7D8', animationDelay: '0.1s' }} />
                    <div className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: '#4AA7D8', animationDelay: '0.2s' }} />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Result */}
          {result && (
            <div className="ocean-glass p-6">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4AA7D8, #D8B15A)' }}>
                  <Sparkles className="w-3.5 h-3.5 text-white" />
                </div>
                <h2 className="text-sm font-semibold" style={{ color: 'rgba(255,255,255,0.9)' }}>AI RESPONSE</h2>
              </div>

              {/* Intent Overview */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                {[
                  { icon: MapPin, label: result.intent.destination },
                  { icon: Calendar, label: result.intent.duration },
                  { icon: Users, label: result.intent.travelers },
                  { icon: Coins, label: result.intent.budget },
                ].map((item, i) => (
                  <div key={i} className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <item.icon className="w-3.5 h-3.5 mb-1.5" style={{ color: '#4AA7D8' }} />
                    <p className="text-xs" style={{ color: 'rgba(255,255,255,0.85)' }}>{item.label}</p>
                  </div>
                ))}
              </div>

              {/* Hotels */}
              <h3 className="text-sm font-medium mb-3" style={{ color: 'rgba(255,255,255,0.7)' }}>推荐酒店</h3>
              <div className="space-y-3 mb-5">
                {result.hotels.map((h, i) => (
                  <div key={i} className="rounded-xl p-4 transition-all duration-200" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium" style={{ color: 'rgba(255,255,255,0.9)' }}>{h.name}</p>
                      <span className="text-xs font-semibold" style={{ color: '#D8B15A' }}>{h.score}</span>
                    </div>
                    <p className="text-xs mb-2" style={{ color: 'rgba(255,255,255,0.5)' }}>¥{h.price}/晚</p>
                    <div className="flex gap-1.5">
                      {h.tags.map(t => (
                        <span key={t} className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.6)' }}>{t}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <button className="btn-ghost flex items-center gap-1.5 text-xs"><RefreshCw className="w-3 h-3" /> 重新生成</button>
                <button className="btn-ghost flex items-center gap-1.5 text-xs"><Download className="w-3 h-3" /> 导出</button>
              </div>
            </div>
          )}

          {/* Empty State */}
          {messages.length === 0 && !isThinking && !result && (
            <div className="text-center py-16">
              <div className="w-14 h-14 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ background: 'rgba(74,167,216,0.1)' }}>
                <Sparkles className="w-6 h-6" style={{ color: 'rgba(74,167,216,0.6)' }} />
              </div>
              <p className="text-sm" style={{ color: 'rgba(255,255,255,0.4)' }}>开始对话，AI 将为你规划行程</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TripStudio;
